import { useState } from "react";
import { Box, Paper, Title, Text, TextInput, Button, Anchor, Flex } from "@mantine/core";
import { IconMail } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import trpc from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util.ts";
import { successNotification } from "../../shared/utils/successNotification";

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  /* ---------------- Validation ---------------- */

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isFormValid = email.trim() !== "" && isValidEmail(email);

  /* ---------------- Submit ---------------- */

  const handleSubmit = async () => {
    if (!isFormValid) return;

    setLoading(true);

    try {
      await trpc.auth.requestPasswordReset.mutate({
        email,
      });

      successNotification({
        title: "Reset link sent",
        message: "If an account exists for this email, a password reset link has been sent.",
      });

      navigate("/login");
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Flex direction="column" align="center" gap="md">
        {/* Header */}
        <Flex direction="column" align="center" gap={4} mb="sm">
          <Title order={1} fz={28} fw={600} c="#2563EB">
            GT Setu
          </Title>
          <Text fz="md" c="#2563EB" fw={500}>
            A Global Trade Sphere Initiative
          </Text>
        </Flex>

        {/* Card */}
        <Paper shadow="md" p="xl" radius="lg" style={{ width: 360 }}>
          <Title order={2} mb="xs">
            Forgot password
          </Title>

          <Text size="sm" c="slate.6" mb="lg">
            Enter your registered email address and we’ll send you a password reset link.
          </Text>

          <TextInput
            label="Email address"
            placeholder="you@example.com"
            required
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            leftSection={<IconMail size={18} color="#6B7280" />}
            error={email && !isValidEmail(email) ? "Please enter a valid email address" : null}
            mb="md"
          />

          <Button fullWidth onClick={handleSubmit} loading={loading} disabled={!isFormValid || loading} mb="md">
            Send reset link
          </Button>

          <Flex justify="center">
            <Anchor component={Link} to="/login" size="sm">
              ← Back to login
            </Anchor>
          </Flex>
        </Paper>
      </Flex>
    </Box>
  );
};

export default ForgotPassword;
